import { Box, Flex, Icon, Image, Text, useColorModeValue } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUploadCloud, FiX } from 'react-icons/fi';

interface Props {
	value: File | null;
	onChange: (file: File | null) => void;
	accept?: string;
	showPreview?: boolean;
	maxSize?: number;
}

export default function FileUploadControl({ value, onChange, accept, showPreview, maxSize }: Props) {
	const [preview, setPreview] = useState<string | null>(null);
	const borderColor = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
	const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
	const iconColor = useColorModeValue('brand.500', 'brand.400');

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (!file) return;
			onChange(file);
			if (showPreview && file.type.startsWith('image/')) {
				const reader = new FileReader();
				reader.onload = (e) => setPreview(e.target?.result as string);
				reader.readAsDataURL(file);
			}
		},
		[onChange, showPreview],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: accept ? { [accept]: [] } : undefined,
		maxSize,
		multiple: false,
	});

	const handleRemove = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange(null);
		setPreview(null);
	};

	if (value && preview && showPreview) {
		return (
			<Box position="relative" borderRadius="16px" overflow="hidden" border="1px solid" borderColor={borderColor}>
				<Image src={preview} alt="Preview" maxH="160px" w="100%" objectFit="cover" />
				<Box
					position="absolute"
					top={2}
					right={2}
					cursor="pointer"
					onClick={handleRemove}
					bg="blackAlpha.600"
					borderRadius="full"
					p={1}
				>
					<Icon as={FiX} color="white" boxSize="14px" />
				</Box>
			</Box>
		);
	}

	return (
		<Flex
			{...getRootProps()}
			direction="column"
			align="center"
			justify="center"
			border="2px dashed"
			borderColor={isDragActive ? 'brand.500' : borderColor}
			borderRadius="16px"
			py={6}
			px={4}
			cursor="pointer"
			transition="all 0.2s ease"
			_hover={{ bg: hoverBg, borderColor: 'brand.400' }}
		>
			<input {...getInputProps()} />
			<Icon as={FiUploadCloud} boxSize="24px" color={iconColor} mb={2} />
			<Text fontSize="sm" color="secondaryGray.600">
				{value ? value.name : isDragActive ? 'Drop file here' : 'Click or drag to upload'}
			</Text>
		</Flex>
	);
}
